import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { generateToken } from "app/services/api/management";
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Alert,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Tabs,
  Tab,
  CircularProgress,
  Avatar,
  Tooltip,
  Grid,
  Chip,
  Switch,
  FormControlLabel,
  Dialog,
  DialogContent,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import RecommendIcon from "@mui/icons-material/Recommend";
import GavelIcon from "@mui/icons-material/Gavel";
import AddBoxIcon from "@mui/icons-material/AddBox";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useSnackbar } from "notistack";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { getQrHighlights, setQrHighlights, getQrCatalog, getQrStickerPreviewUrl } from "app/services/api/qrOrders";
import { TERMS_FIELDS, termsDefaults, termsComplete, renderTerms } from "./qrTermsTemplate";
import { WEB_TERMS_FIELDS, webTermsDefaults, webTermsComplete, renderWebTerms } from "./webTermsTemplate";
import { UploadComponent } from "app/pages/presentation/module/form/types";
import { uploadToImagekit } from "app/services/api/media";
import { PROD_URL } from "app/services/config";
import Images from "app/pages/media/images/Images";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import LanguageIcon from "@mui/icons-material/Language";

// Terms are configured per channel; each channel has its own field schema + template.
// QR (table ordering) → qrTermsTemplate; Web shop → webTermsTemplate (rozvoz/osobný odber, zóna, …).
const termsFieldsFor = (ch) => (ch === "web" ? WEB_TERMS_FIELDS : TERMS_FIELDS);
const termsDefaultsFor = (ch) => (ch === "web" ? webTermsDefaults() : termsDefaults());
const termsCompleteFor = (ch) => (ch === "web" ? webTermsComplete : termsComplete);
const renderTermsFor = (ch) => (ch === "web" ? renderWebTerms : renderTerms);

const LANG_LABEL = { sk: "SK", en: "EN", cs: "CS", de: "DE" };

// QR sticker design templates (backend renders them; see the live preview below the picker).
const QR_STICKER_TEMPLATES = [
  { key: "minimal", label: "Design 1" },
  { key: "classic", label: "Design 2" },
  { key: "botanical", label: "Design 3" },
];

let UID = 0;
const withUid = (x) => ({ type: "product", refId: "", name: {}, description: {}, ...x, _uid: `h${++UID}` });

// F2 „Web nastavenia" tab — stored in dsokQrHighlights.web (same place/endpoint as the other tabs).
const WEB_DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const emptyWebDay = () => ({ open: true, from: "", to: "" });
// Delivery-zone entry types + selectable VAT rates (mirror backend helper/qr/qrSettings.js).
const ZONE_ENTRY_TYPES = ["street", "district", "city"];
const ZONE_VAT_RATES = [23, 20, 19, 10, 5, 0];
const emptyZone = () => ({
  id: `z_${Math.random().toString(36).slice(2, 9)}`,
  name: "",
  fee: 0,
  vatRate: ZONE_VAT_RATES[0],
  minOrder: 0,
  entries: [],
});
const emptyZoneEntry = () => ({ type: "street", name: "" });
/** Normalize one stored zone to the form shape (defaults for any missing field). */
const loadZone = (z) => {
  const o = z && typeof z === "object" ? z : {};
  const entries = (Array.isArray(o.entries) ? o.entries : [])
    .map((e) => ({
      type: ZONE_ENTRY_TYPES.includes(e && e.type) ? e.type : "street",
      name: typeof (e && e.name) === "string" ? e.name : "",
    }));
  return {
    id: typeof o.id === "string" && o.id ? o.id : `z_${Math.random().toString(36).slice(2, 9)}`,
    name: typeof o.name === "string" ? o.name : "",
    fee: Number(o.fee) >= 0 ? Number(o.fee) : 0,
    vatRate: ZONE_VAT_RATES.includes(Number(o.vatRate)) ? Number(o.vatRate) : ZONE_VAT_RATES[0],
    minOrder: Number(o.minOrder) >= 0 ? Number(o.minOrder) : 0,
    entries,
  };
};
// F2 info cards (home): 4 configurable cards — find (Kde nás nájdete), pickup (Osobný odber),
// delivery (Rozvoz), payment (Platba). Each has an editable title + text per language; empty → the
// web falls back to its automatic content.
const INFO_KEYS = ["find", "pickup", "delivery", "payment"];
const emptyInfoCard = () => ({ title: {}, text: {} });
const defaultInfo = () => INFO_KEYS.reduce((acc, k) => ({ ...acc, [k]: emptyInfoCard() }), {});
const loadInfoCard = (c) => {
  const o = c && typeof c === "object" ? c : {};
  const lm = (m) => (m && typeof m === "object" ? { ...m } : {});
  return { title: lm(o.title), text: lm(o.text) };
};
const loadInfo = (v) => {
  const o = v && typeof v === "object" ? v : {};
  return INFO_KEYS.reduce((acc, k) => ({ ...acc, [k]: loadInfoCard(o[k]) }), {});
};
const defaultWeb = () => ({
  enabled: false,
  // Browser tab title (document.title) for the web shop. Empty → „Datavision web order".
  pageTitle: "",
  // Company name shown on the web home (near the slider). Empty → the venue name is used.
  companyName: "",
  // Web logo (picked from the system images). Empty → the QR logo is used as the default.
  logo: "",
  info: defaultInfo(),
  address: { name: "", street: "", postcode: "", city: "" },
  openingHours: WEB_DAYS.reduce((acc, d) => ({ ...acc, [d]: emptyWebDay() }), {}),
  delivery: { fee: 0, minOrder: 0, freeDelivery: false, freeFrom: 0, zonesEnabled: false, zones: [] },
  time: { asap: { enabled: true, minutes: 20 }, scheduled: { enabled: false }, slotMinutes: 15 },
  // F2: configurable home-slider gallery — each image has a name + description per language.
  gallery: [],
});
/** Normalize one stored gallery item to the form shape. */
const loadGalleryItem = (g) => {
  const o = g && typeof g === "object" ? g : {};
  const langMap = (m) => (m && typeof m === "object" ? { ...m } : {});
  return {
    id: typeof o.id === "string" && o.id ? o.id : `g_${Math.random().toString(36).slice(2, 9)}`,
    image: typeof o.image === "string" ? o.image : "",
    name: langMap(o.name),
    description: langMap(o.description),
  };
};
const emptyGalleryItem = () => ({ id: `g_${Math.random().toString(36).slice(2, 9)}`, image: "", name: {}, description: {} });
/** Merge a loaded/stored web block over the defaults so the form always has every field. */
const loadWeb = (w) => {
  const d = defaultWeb();
  if (!w || typeof w !== "object") return d;
  const oh = w.openingHours && typeof w.openingHours === "object" ? w.openingHours : {};
  const del = w.delivery && typeof w.delivery === "object" ? w.delivery : {};
  const t = w.time && typeof w.time === "object" ? w.time : {};
  const asap = t.asap && typeof t.asap === "object" ? t.asap : {};
  const sched = t.scheduled && typeof t.scheduled === "object" ? t.scheduled : {};
  return {
    enabled: w.enabled === true,
    pageTitle: typeof w.pageTitle === "string" ? w.pageTitle : "",
    companyName: typeof w.companyName === "string" ? w.companyName : "",
    logo: typeof w.logo === "string" ? w.logo : "",
    info: loadInfo(w.info),
    address: { ...d.address, ...(w.address && typeof w.address === "object" ? w.address : {}) },
    openingHours: WEB_DAYS.reduce(
      (acc, day) => ({ ...acc, [day]: { ...emptyWebDay(), ...(oh[day] && typeof oh[day] === "object" ? oh[day] : {}) } }),
      {}
    ),
    delivery: {
      fee: Number(del.fee) >= 0 ? Number(del.fee) : 0,
      minOrder: Number(del.minOrder) >= 0 ? Number(del.minOrder) : 0,
      freeDelivery: del.freeDelivery === true,
      freeFrom: del.freeDelivery === true && Number(del.freeFrom) > 0 ? Number(del.freeFrom) : 0,
      zonesEnabled: del.zonesEnabled === true,
      zones: (Array.isArray(del.zones) ? del.zones : []).map(loadZone),
    },
    time: {
      asap: { enabled: asap.enabled !== false, minutes: Number(asap.minutes) > 0 ? Number(asap.minutes) : 20 },
      scheduled: { enabled: sched.enabled === true },
      slotMinutes: Number(t.slotMinutes) > 0 ? Number(t.slotMinutes) : 15,
    },
    gallery: (Array.isArray(w.gallery) ? w.gallery : []).map(loadGalleryItem),
  };
};

/**
 * One sortable highlight row (module-level so a stable `_uid` key preserves input focus across
 * re-renders). Dragging is only via the handle so the text fields stay editable.
 */
const SortableRow = ({ item, index, allowCategory, showDescription, optionsFor, findOption, activeLang, t, onPatch, onRemove, onSetText }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item._uid });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const opt = findOption(item.type, item.refId);
  return (
    <Card ref={setNodeRef} style={style} variant="outlined" sx={{ mb: 1.5 }}>
      <CardContent sx={{ pb: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Tooltip arrow title={t("pages.qrSettings.drag", { defaultValue: "Potiahnutím zmeníte poradie" })}>
            <Box
              {...attributes}
              {...listeners}
              sx={{ cursor: "grab", "&:active": { cursor: "grabbing" }, color: "text.secondary", pt: 1, touchAction: "none" }}
            >
              <DragIndicatorIcon />
            </Box>
          </Tooltip>
          <Avatar
            variant="rounded"
            src={opt?.image || undefined}
            sx={{ width: 56, height: 56, bgcolor: "action.hover", color: "text.disabled", fontSize: 12 }}
          >
            {opt?.image ? null : index + 1}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 1.5 }}>
              {allowCategory ? (
                <TextField
                  select size="small" sx={{ minWidth: 140 }}
                  label={t("pages.qrSettings.type", { defaultValue: "Typ" })}
                  value={item.type}
                  onChange={(e) => onPatch(index, { type: e.target.value, refId: "" })}
                >
                  <MenuItem value="product">{t("pages.qrSettings.typeProduct", { defaultValue: "Produkt" })}</MenuItem>
                  <MenuItem value="category">{t("pages.qrSettings.typeCategory", { defaultValue: "Kategória" })}</MenuItem>
                </TextField>
              ) : null}
              <Autocomplete
                fullWidth size="small"
                options={optionsFor(item.type)}
                value={opt}
                getOptionLabel={(o) => (o && o.name) || ""}
                isOptionEqualToValue={(o, v) => String(o.id) === String(v?.id)}
                onChange={(_, v) => {
                  const patch = { refId: v ? String(v.id) : "" };
                  // Auto-carry the picked item's name in the active language. Only fill when the
                  // field is still empty so a manually typed override is never overwritten.
                  const current = (item.name && item.name[activeLang]) || "";
                  if (v && v.name && !current.trim()) {
                    patch.name = { ...(item.name || {}), [activeLang]: v.name };
                  }
                  onPatch(index, patch);
                }}
                renderOption={(props, o) => (
                  <Box component="li" {...props} key={o.id} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Avatar variant="rounded" src={o.image || undefined} sx={{ width: 28, height: 28 }} />
                    <span>{o.name || o.id}</span>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={item.type === "category"
                      ? t("pages.qrSettings.pickCategory", { defaultValue: "Vyberte kategóriu" })
                      : t("pages.qrSettings.pickProduct", { defaultValue: "Vyberte produkt" })}
                  />
                )}
              />
              <Tooltip arrow title={t("pages.qrSettings.remove", { defaultValue: "Odstrániť" })}>
                <IconButton color="error" onClick={() => onRemove(index)} sx={{ alignSelf: "center" }}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Stack>
            <Stack spacing={1}>
              <TextField
                size="small" fullWidth
                label={`${t("pages.qrSettings.name", { defaultValue: "Názov" })} (${LANG_LABEL[activeLang] || activeLang})`}
                value={(item.name && item.name[activeLang]) || ""}
                onChange={(e) => onSetText(index, "name", e.target.value)}
              />
              {showDescription ? (
                <TextField
                  size="small" fullWidth multiline minRows={2}
                  label={`${t("pages.qrSettings.description", { defaultValue: "Popis" })} (${LANG_LABEL[activeLang] || activeLang})`}
                  value={(item.description && item.description[activeLang]) || ""}
                  onChange={(e) => onSetText(index, "description", e.target.value)}
                />
              ) : null}
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

/**
 * QR objednávanie → Nastavenia (DSOK-475): configurable „Tipy pre Vás" (product|category) and
 * „Odporúčame" (products only) shown on the QR guest web home. Name/description per enabled
 * language (empty → main language on the web). Order is set by drag & drop.
 */
const QrSettings = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { getAuthUser } = useJumboAuth();
  const authUser = getAuthUser() || {};
  const customerId = authUser?.customer?.id ?? authUser?.customerId ?? authUser?.id;
  const tokenRef = useRef(undefined);
  const tok = useCallback(async () => {
    if (tokenRef.current !== undefined) return tokenRef.current || undefined;
    if (!customerId) { tokenRef.current = null; return undefined; }
    try { const got = await generateToken(customerId); tokenRef.current = got || null; return got || undefined; }
    catch { tokenRef.current = null; return undefined; }
  }, [customerId]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [languages, setLanguages] = useState(["sk"]);
  const [defaultLanguage, setDefaultLanguage] = useState("sk");
  const [limits, setLimits] = useState({ tips: 5, recommendations: 10 });
  const [activeLang, setActiveLang] = useState("sk");
  // Content tab: 0 = Tipy pre Vás, 1 = Odporúčame, 2 = Obchodné podmienky.
  const [activeTab, setActiveTab] = useState(0);
  const [tips, setTips] = useState([]);
  const [recs, setRecs] = useState([]);
  // Terms (Obchodné podmienky, SK only): the filled variables, the generated/edited HTML, whether it
  // was hand-edited after generation, and best-effort prefill (seller identity) from the backend.
  const [termsVars, setTermsVars] = useState(termsDefaults());
  const [termsHtml, setTermsHtml] = useState("");
  const [termsCustomized, setTermsCustomized] = useState(false);
  const [termsPrefill, setTermsPrefill] = useState({});
  // F2: terms are configured per channel — „qr" (QR table flow) and „web" (web shop), stored
  // separately. The editor binds to the active-channel form state; the other channel lives in
  // termsRef and is swapped in on toggle (same pattern as byMode for tips).
  const [termsChannel, setTermsChannel] = useState("qr");
  const termsRef = useRef({
    qr: { vars: termsDefaults(), html: "", customized: false },
    web: { vars: webTermsDefaults(), html: "", customized: false },
  });
  // QR-code print-sheet settings („Nastavenia tlače QR kódov").
  const [printSettings, setPrintSettings] = useState({ template: "minimal", logo: "", heading: "", description: "" });
  const [previewUrl, setPreviewUrl] = useState("");
  // Cart upsell („Pridáte si?"): toggle + the menu categories to suggest from.
  const [upsell, setUpsell] = useState({ enabled: false, categoryIds: [] });
  // F2 „Web nastavenia": web ordering enabled, venue address, weekly opening hours, fulfillment time.
  const [web, setWeb] = useState(defaultWeb());
  // F2 Web nastavenia — inner sub-tabs: 0 Všeobecné, 1 Rozvoz, 2 Hodiny a čas, 3 Obrázky.
  const [webTab, setWebTab] = useState(0);
  // Gallery images use the full system media page (/media/images): browse folders, upload, select —
  // same component as the category image field. `imgPickerFor` = the gallery index being set.
  const [imgPickerFor, setImgPickerFor] = useState(null);
  // Whether web ordering is provisioned in the customer detail (customer.web.enabled). The „Web
  // nastavenia" tab is shown only when true.
  const [webProvisioned, setWebProvisioned] = useState(false);
  // F2: Home content (tips/recommendations/upsell) is configured PER consumption mode (dine/take).
  // The flat tips/recs/upsell above hold the CURRENTLY edited mode; the other mode lives in byModeRef.
  const [editMode, setEditMode] = useState("dine");
  // F2: which consumption modes the customer has enabled (from customer.qrOrders.modes). A single
  // enabled mode hides the dine/take selector and edits only that mode.
  const [enabledModes, setEnabledModes] = useState(["dine"]);
  const byModeRef = useRef({
    dine: { tips: [], recs: [], upsell: { enabled: false, categoryIds: [] } },
    take: { tips: [], recs: [], upsell: { enabled: false, categoryIds: [] } },
  });
  const loadModeBlock = (b) => ({
    tips: Array.isArray(b && b.tips) ? b.tips.map(withUid) : [],
    recs: Array.isArray(b && b.recommendations) ? b.recommendations.map(withUid) : [],
    upsell: {
      enabled: !!(b && b.upsell && b.upsell.enabled === true),
      categoryIds: b && b.upsell && Array.isArray(b.upsell.categoryIds) ? b.upsell.categoryIds : [],
    },
  });
  const applyModeToForm = (m) => {
    const cur = byModeRef.current[m] || byModeRef.current.dine;
    setTips(cur.tips);
    setRecs(cur.recs);
    setUpsell(cur.upsell);
  };
  // Last generated/loaded HTML — an editor change that differs from it marks the text as hand-edited.
  const generatedRef = useRef("");
  const [catalog, setCatalog] = useState({ categories: [], products: [] });
  // F2: the picker catalog is per mode (Na mieste → DINE_IN menu, So sebou → TAKEAWAY menu) AND per
  // language (option names come back localized, so a pick can carry the name in the active language).
  // Cache by `${mode}:${lang}` so switching mode/language doesn't refetch.
  const catalogCache = useRef({});
  const loadCatalog = useCallback(async (m, lang) => {
    const key = `${m}:${lang}`;
    if (catalogCache.current[key]) { setCatalog(catalogCache.current[key]); return; }
    try {
      const token = await tok();
      const cat = await getQrCatalog(lang || "sk", token, m);
      catalogCache.current[key] = cat || { categories: [], products: [] };
      setCatalog(catalogCache.current[key]);
    } catch { /* keep the current catalog on failure */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const switchMode = (m) => {
    if (!m || m === editMode) return;
    byModeRef.current[editMode] = { tips, recs, upsell };
    applyModeToForm(m);
    setEditMode(m);
    loadCatalog(m, activeLang);
  };
  // Swap the terms editor between the QR and the Web channel, preserving unsaved edits of each.
  const switchTermsChannel = (ch) => {
    if (!ch || ch === termsChannel) return;
    termsRef.current[termsChannel] = { vars: termsVars, html: termsHtml, customized: termsCustomized };
    const next = termsRef.current[ch] || { vars: termsDefaultsFor(ch), html: "", customized: false };
    setTermsVars(next.vars);
    setTermsHtml(next.html);
    generatedRef.current = next.html;
    setTermsCustomized(next.customized);
    setTermsChannel(ch);
  };

  // Web nastavenia → Obchodné podmienky sub-tab always edits the WEB terms channel.
  useEffect(() => {
    if (activeTab === 5 && webTab === 4 && termsChannel !== "web") switchTermsChannel("web");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, webTab]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const token = await tok();
        const h = await getQrHighlights(token);
        if (!alive) return;
        const langs = Array.isArray(h?.languages) && h.languages.length ? h.languages : ["sk"];
        const def = h?.defaultLanguage || langs[0] || "sk";
        setLanguages(langs);
        setDefaultLanguage(def);
        setActiveLang(def);
        if (h?.limits) setLimits(h.limits);
        // F2: per-mode Home content. Load both modes into byModeRef; edit the first ENABLED mode.
        const bm = (h && h.byMode && typeof h.byMode === "object") ? h.byMode : {};
        byModeRef.current = { dine: loadModeBlock(bm.dine), take: loadModeBlock(bm.take) };
        const em = Array.isArray(h?.enabledModes) && h.enabledModes.length
          ? h.enabledModes.filter((m) => m === "dine" || m === "take")
          : ["dine"];
        const emSafe = em.length ? em : ["dine"];
        const firstMode = emSafe[0];
        setEnabledModes(emSafe);
        setEditMode(firstMode);
        applyModeToForm(firstMode);
        // Terms: merge saved vars over defaults, then seed any still-empty seller field from prefill.
        // Done per channel (qr + web) — each has its own vars/html/customized stored separately.
        const prefill = (h && h.termsPrefill && typeof h.termsPrefill === "object") ? h.termsPrefill : {};
        setTermsPrefill(prefill);
        const mergeVars = (savedVarsRaw, ch) => {
          const savedVars = savedVarsRaw && typeof savedVarsRaw === "object" ? savedVarsRaw : {};
          const merged = { ...termsDefaultsFor(ch), ...savedVars };
          for (const k of Object.keys(prefill)) {
            if (!String(merged[k] || "").trim() && prefill[k]) merged[k] = prefill[k];
          }
          return merged;
        };
        const qrVars = mergeVars(h && h.termsVars, "qr");
        const qrHtml = (h && h.terms && (h.terms.sk || h.terms[def])) || "";
        const webVars = mergeVars(h && h.webTermsVars, "web");
        const webHtml = (h && h.webTerms && (h.webTerms.sk || h.webTerms[def])) || "";
        termsRef.current = {
          qr: { vars: qrVars, html: qrHtml, customized: h?.termsCustomized === true },
          web: { vars: webVars, html: webHtml, customized: h?.webTermsCustomized === true },
        };
        // Apply the active channel (qr by default) to the editor form.
        const curTc = termsRef.current[termsChannel] || termsRef.current.qr;
        setTermsVars(curTc.vars);
        setTermsHtml(curTc.html);
        generatedRef.current = curTc.html;
        setTermsCustomized(curTc.customized);
        setPrintSettings({
          template: "minimal",
          logo: "",
          heading: "",
          description: "",
          ...(h && h.printSettings && typeof h.printSettings === "object" ? h.printSettings : {}),
        });
        setWeb(loadWeb(h && h.web));
        setWebProvisioned(!!(h && h.webProvisioned));
        // Picker catalog for the first enabled mode in the default language; cached by `${mode}:${lang}`
        // so switching mode/language is instant.
        const cat = await getQrCatalog(def, token, firstMode);
        if (alive) {
          catalogCache.current = { [`${firstMode}:${def}`]: cat || { categories: [], products: [] } };
          setCatalog(cat || { categories: [], products: [] });
        }
      } catch (e) {
        if (alive) enqueueSnackbar(t("pages.qrSettings.loadError", { defaultValue: "Nepodarilo sa načítať nastavenia." }), { variant: "error" });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live sticker preview for the print tab — debounced; reflects the current (unsaved) print settings.
  useEffect(() => {
    if (activeTab !== 3) return undefined;
    let alive = true;
    const handle = setTimeout(async () => {
      try {
        const token = await tok();
        const u = await getQrStickerPreviewUrl(printSettings, token);
        if (!alive) {
          try { URL.revokeObjectURL(u); } catch (e) {}
          return;
        }
        setPreviewUrl((prev) => {
          if (prev) { try { URL.revokeObjectURL(prev); } catch (e) {} }
          return u;
        });
      } catch (e) {
        /* ignore preview errors */
      }
    }, 400);
    return () => { alive = false; clearTimeout(handle); };
  }, [activeTab, printSettings, tok]);

  const save = async () => {
    // Free delivery is a single CENTRAL (global) threshold — require its amount when the toggle is on.
    const del = web.delivery || {};
    const freeMissing = del.freeDelivery === true && !(Number(del.freeFrom) > 0);
    if (freeMissing) {
      enqueueSnackbar(
        t("pages.qrSettings.web.freeFromMissing", {
          defaultValue: "Pri zapnutej doprave zdarma zadajte sumu, od ktorej platí.",
        }),
        { variant: "error" }
      );
      return;
    }
    setSaving(true);
    try {
      // _uid is client-only; the backend sanitizer drops unknown fields. Terms are SK-only (one
      // combined VOP+GDPR document); stored under terms.sk so the guest app modal reads it as-is.
      // F2: sync the currently edited mode into byModeRef, then save BOTH modes under byMode.
      byModeRef.current[editMode] = { tips, recs, upsell };
      const bmPayload = {
        dine: {
          tips: byModeRef.current.dine.tips,
          recommendations: byModeRef.current.dine.recs,
          upsell: byModeRef.current.dine.upsell,
        },
        take: {
          tips: byModeRef.current.take.tips,
          recommendations: byModeRef.current.take.recs,
          upsell: byModeRef.current.take.upsell,
        },
      };
      // Sync the currently edited terms channel into the ref, then send BOTH channels (qr + web).
      termsRef.current[termsChannel] = { vars: termsVars, html: termsHtml, customized: termsCustomized };
      const qrT = termsRef.current.qr;
      const webT = termsRef.current.web;
      const stored = await setQrHighlights(
        {
          byMode: bmPayload,
          terms: { sk: qrT.html }, termsVars: qrT.vars, termsCustomized: qrT.customized,
          webTerms: { sk: webT.html }, webTermsVars: webT.vars, webTermsCustomized: webT.customized,
          printSettings, web,
        },
        await tok()
      );
      // Re-hydrate both modes from the sanitized response and re-apply the edited mode to the form.
      const sb = (stored && stored.byMode && typeof stored.byMode === "object") ? stored.byMode : {};
      byModeRef.current = { dine: loadModeBlock(sb.dine), take: loadModeBlock(sb.take) };
      applyModeToForm(editMode);
      // Re-hydrate both terms channels from the sanitized response, keep the active channel on screen.
      termsRef.current = {
        qr: {
          vars: { ...termsDefaults(), ...((stored && stored.termsVars) || {}) },
          html: (stored && stored.terms && stored.terms.sk) || "",
          customized: stored?.termsCustomized === true,
        },
        web: {
          vars: { ...webTermsDefaults(), ...((stored && stored.webTermsVars) || {}) },
          html: (stored && stored.webTerms && stored.webTerms.sk) || "",
          customized: stored?.webTermsCustomized === true,
        },
      };
      const curT = termsRef.current[termsChannel] || termsRef.current.qr;
      setTermsHtml(curT.html);
      generatedRef.current = curT.html;
      setTermsVars(curT.vars);
      setTermsCustomized(curT.customized);
      if (stored && stored.printSettings && typeof stored.printSettings === "object") {
        setPrintSettings({ template: "minimal", logo: "", heading: "", description: "", ...stored.printSettings });
      }
      if (stored && stored.web && typeof stored.web === "object") setWeb(loadWeb(stored.web));
      enqueueSnackbar(t("pages.qrSettings.saved", { defaultValue: "Uložené." }), { variant: "success" });
    } catch (e) {
      enqueueSnackbar(t("pages.qrSettings.saveError", { defaultValue: "Nepodarilo sa uložiť." }), { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const optionsFor = useCallback((type) => (type === "category" ? catalog.categories : catalog.products), [catalog]);
  const findOption = useCallback((type, refId) => optionsFor(type).find((o) => String(o.id) === String(refId)) || null, [optionsFor]);

  const makeHandlers = (list, setList, isTips) => ({
    add: () => setList([...list, withUid(isTips ? {} : { type: "product" })]),
    remove: (i) => setList(list.filter((_, idx) => idx !== i)),
    patch: (i, p) => setList(list.map((it, idx) => (idx === i ? { ...it, ...p } : it))),
    setText: (i, field, value) =>
      setList(list.map((it, idx) => (idx === i ? { ...it, [field]: { ...(it[field] || {}), [activeLang]: value } } : it))),
    dragEnd: (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = list.findIndex((x) => x._uid === active.id);
      const newIndex = list.findIndex((x) => x._uid === over.id);
      if (oldIndex !== -1 && newIndex !== -1) setList(arrayMove(list, oldIndex, newIndex));
    },
  });

  const section = ({ title, help, icon, list, setList, max, allowCategory, isTips }) => {
    const handlers = makeHandlers(list, setList, isTips);
    return (
      <Card>
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
            {icon}
            <Typography variant="h4" sx={{ m: 0, flexGrow: 1 }}>{title}</Typography>
            <Typography variant="caption" color="text.secondary">{list.length} / {max}</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{help}</Typography>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handlers.dragEnd}>
            <SortableContext items={list.map((x) => x._uid)} strategy={verticalListSortingStrategy}>
              {list.map((it, i) => (
                <SortableRow
                  key={it._uid}
                  item={it}
                  index={i}
                  allowCategory={allowCategory}
                  showDescription={isTips}
                  optionsFor={optionsFor}
                  findOption={findOption}
                  activeLang={activeLang}
                  t={t}
                  onPatch={handlers.patch}
                  onRemove={handlers.remove}
                  onSetText={handlers.setText}
                />
              ))}
            </SortableContext>
          </DndContext>
          <Button startIcon={<AddBoxIcon />} onClick={handlers.add} disabled={list.length >= max} sx={{ mt: 0.5 }}>
            {t("pages.qrSettings.add", { defaultValue: "Pridať" })}
          </Button>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  // Terms editor (form → generate → WYSIWYG), reused by the dedicated „Obchodné podmienky" tab (with
  // the QR/Web channel toggle) and by the Web nastavenia → Obchodné podmienky sub-tab (fixed to web).
  const renderTermsEditor = ({ showChannelToggle }) => {
    // Field schema + validator + renderer depend on the active channel (QR vs Web shop).
    const fields = termsFieldsFor(termsChannel);
    const { ok: canGenerate, missing } = termsCompleteFor(termsChannel)(termsVars);
    const setVar = (key, val) => setTermsVars((p) => ({ ...p, [key]: val }));
    const generate = () => {
      const html = renderTermsFor(termsChannel)(termsVars);
      generatedRef.current = html;
      setTermsHtml(html);
      setTermsCustomized(false);
    };
    const onEditHtml = (content) => {
      setTermsHtml(content);
      if (content !== generatedRef.current) setTermsCustomized(true);
    };
    return (
      <Card>
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
            <GavelIcon color="primary" />
            <Typography variant="h4" sx={{ m: 0, flexGrow: 1 }}>
              {t("pages.qrSettings.terms.title", { defaultValue: "Obchodné podmienky" })}
            </Typography>
            {termsCustomized ? (
              <Chip size="small" color="warning" label={t("pages.qrSettings.terms.customized", { defaultValue: "Upravené ručne" })} />
            ) : null}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("pages.qrSettings.terms.help2", {
              defaultValue:
                "Vyplňte údaje prevádzky, vygenerujte text zo šablóny a podľa potreby ho upravte. Po uložení sa hosťovi zobrazí v okne pri objednávke a registrácii. Údaje prevádzkovateľa systému (Datavision) sú doplnené automaticky.",
            })}
          </Typography>

          {showChannelToggle ? (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {t("pages.qrSettings.terms.channelFor", { defaultValue: "Podmienky pre:" })}
                </Typography>
                <Button size="small" variant={termsChannel === "qr" ? "contained" : "outlined"} onClick={() => switchTermsChannel("qr")}>
                  {t("pages.qrSettings.terms.channelQr", { defaultValue: "QR objednávanie" })}
                </Button>
                <Button size="small" variant={termsChannel === "web" ? "contained" : "outlined"} onClick={() => switchTermsChannel("web")}>
                  {t("pages.qrSettings.terms.channelWeb", { defaultValue: "Web objednávanie" })}
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                {t("pages.qrSettings.terms.channelHint", {
                  defaultValue: "QR a web majú samostatné obchodné podmienky. Ak web podmienky nevyplníte, na webe sa použijú QR podmienky.",
                })}
              </Typography>
            </>
          ) : null}

          <Grid container spacing={2}>
            {fields.map((f) => {
              let node;
              if (f.type === "bool") {
                node = (
                  <FormControlLabel
                    control={<Switch checked={termsVars[f.key] === true} onChange={(e) => setVar(f.key, e.target.checked)} />}
                    label={f.label}
                  />
                );
              } else if (f.type === "number") {
                node = (
                  <TextField
                    type="number" size="small" fullWidth label={f.label}
                    value={termsVars[f.key] ?? ""} inputProps={{ min: f.min ?? 0 }}
                    onChange={(e) => setVar(f.key, e.target.value === "" ? "" : Number(e.target.value))}
                  />
                );
              } else if (f.type === "richtext") {
                node = (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{f.label}</Typography>
                    <ReactQuill
                      value={termsVars[f.key] || ""}
                      onChange={(c) => setVar(f.key, c === "<p><br></p>" ? "" : c)}
                      modules={{ toolbar: [["bold", "italic"], [{ list: "ordered" }, { list: "bullet" }], ["link"], ["clean"]] }}
                    />
                  </Box>
                );
              } else {
                node = (
                  <TextField
                    size="small" fullWidth label={f.label}
                    value={termsVars[f.key] || ""} helperText={f.help || ""}
                    onChange={(e) => setVar(f.key, e.target.value)}
                  />
                );
              }
              const colSpan = f.type === "bool" || f.type === "number" ? 6 : 12;
              return (
                <React.Fragment key={f.key}>
                  {f.group ? (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>{f.group}</Typography>
                    </Grid>
                  ) : null}
                  <Grid item xs={12} md={colSpan}>{node}</Grid>
                </React.Fragment>
              );
            })}
          </Grid>

          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2, mb: 1.5 }}>
            <Tooltip arrow title={canGenerate ? "" : `${t("pages.qrSettings.terms.missingLbl", { defaultValue: "Chýba" })}: ${missing.join(", ")}`}>
              <span>
                <Button variant="contained" startIcon={<GavelIcon />} disabled={!canGenerate} onClick={generate}>
                  {t("pages.qrSettings.terms.generate", { defaultValue: "Vygenerovať zo šablóny" })}
                </Button>
              </span>
            </Tooltip>
            {!canGenerate ? (
              <Typography variant="caption" color="text.secondary">
                {t("pages.qrSettings.terms.generateHint", { defaultValue: "Aktívne po vyplnení všetkých povinných polí." })}
              </Typography>
            ) : null}
          </Stack>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, mt: 1 }}>
            {t("pages.qrSettings.terms.editorTitle", { defaultValue: "Text (obchodné podmienky + ochrana údajov)" })}
          </Typography>
          <ReactQuill
            value={termsHtml}
            onChange={onEditHtml}
            modules={{
              toolbar: [
                [{ header: [2, 3, false] }],
                ["bold", "italic", "underline"],
                [{ list: "ordered" }, { list: "bullet" }],
                ["link"],
                ["clean"],
              ],
            }}
          />
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="flex-start" sx={{ mb: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h2" sx={{ mb: 0.5 }}>
            {t("pages.qrSettings.title", { defaultValue: "QR objednávanie – Nastavenia" })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("pages.qrSettings.subtitle", { defaultValue: "Obsah zobrazený hosťovi na QR stránke (Tipy pre Vás, Odporúčame, Obchodné podmienky)." })}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={save} disabled={saving}>
          {t("pages.qrSettings.save", { defaultValue: "Uložiť" })}
        </Button>
      </Stack>

      {/* Content tabs: Tipy pre Vás / Odporúčame / Obchodné podmienky. */}
      <Box sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab icon={<TipsAndUpdatesIcon />} iconPosition="start" label={t("pages.qrSettings.tips.title", { defaultValue: "Tipy pre Vás" })} />
          <Tab icon={<RecommendIcon />} iconPosition="start" label={t("pages.qrSettings.recommendations.title", { defaultValue: "Odporúčame" })} />
          <Tab icon={<GavelIcon />} iconPosition="start" label={t("pages.qrSettings.terms.title", { defaultValue: "Obchodné podmienky" })} />
          <Tab icon={<QrCode2Icon />} iconPosition="start" label={t("pages.qrSettings.print.title", { defaultValue: "Tlač QR kódov" })} />
          <Tab icon={<AddShoppingCartIcon />} iconPosition="start" label={t("pages.qrSettings.upsell.title", { defaultValue: "Upsell v košíku" })} />
          {webProvisioned ? (
            <Tab icon={<LanguageIcon />} iconPosition="start" label={t("pages.qrSettings.web.title", { defaultValue: "Web nastavenia" })} />
          ) : null}
        </Tabs>
      </Box>

      {/* F2: mode selector — Tipy/Odporúčame/Upsell are configured separately for „Na mieste" and
          „So sebou" (each mode has its own menu and Home content). Terms + print are shared. Shown
          only when the customer has BOTH modes enabled; a single-mode customer edits that one mode. */}
      {(activeTab === 0 || activeTab === 1 || activeTab === 4) && enabledModes.length > 1 ? (
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="body2" color="text.secondary">
            {t("pages.qrSettings.modeFor", { defaultValue: "Pre spôsob:" })}
          </Typography>
          {enabledModes.map((m) => (
            <Button
              key={m}
              size="small"
              variant={editMode === m ? "contained" : "outlined"}
              onClick={() => switchMode(m)}
            >
              {m === "take"
                ? t("pages.qrSettings.modeTake", { defaultValue: "So sebou" })
                : t("pages.qrSettings.modeDine", { defaultValue: "Na mieste" })}
            </Button>
          ))}
        </Box>
      ) : null}

      {/* Language selector — applies to Tipy/Odporúčame (name/description). Terms + print are SK-only. */}
      {languages.length > 1 && activeTab !== 2 && activeTab !== 3 && activeTab !== 4 && activeTab !== 5 ? (
        <Box sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={activeLang} onChange={(_, v) => { setActiveLang(v); loadCatalog(editMode, v); }} variant="scrollable" scrollButtons="auto">
            {languages.map((l) => (
              <Tab key={l} value={l} label={LANG_LABEL[l] || l.toUpperCase()} />
            ))}
          </Tabs>
        </Box>
      ) : null}

      {activeTab !== 2 && activeTab !== 3 && activeTab !== 4 && activeTab !== 5 ? (
        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
          {t("pages.qrSettings.langHint", {
            defaultValue: "Názov a popis vyplňte vo všetkých jazykoch. Ak preklad nevyplníte, na QR stránke sa zobrazí hlavný jazyk. Poradie zmeníte potiahnutím.",
            lng: defaultLanguage,
          })}
        </Alert>
      ) : null}

      {activeTab === 0
        ? section({
            title: t("pages.qrSettings.tips.title", { defaultValue: "Tipy pre Vás" }),
            help: t("pages.qrSettings.tips.help", { defaultValue: "Až 5 tipov. Typ produkt alebo kategória; na QR stránke sa zobrazí názov, popis a obrázok vybranej položky a po kliknutí prejde na ňu." }),
            icon: <TipsAndUpdatesIcon color="primary" />,
            list: tips, setList: setTips, max: limits.tips || 5, allowCategory: true, isTips: true,
          })
        : null}

      {activeTab === 1
        ? section({
            title: t("pages.qrSettings.recommendations.title", { defaultValue: "Odporúčame" }),
            help: t("pages.qrSettings.recommendations.help", { defaultValue: "Rovnaká logika ako Tipy pre Vás, ale len s výberom produktov." }),
            icon: <RecommendIcon color="primary" />,
            list: recs, setList: setRecs, max: limits.recommendations || 10, allowCategory: false, isTips: false,
          })
        : null}

      {activeTab === 2 ? renderTermsEditor({ showChannelToggle: true }) : null}

      {activeTab === 3 ? (
        <Card>
          <CardContent>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
              <QrCode2Icon color="primary" />
              <Typography variant="h4" sx={{ m: 0, flexGrow: 1 }}>
                {t("pages.qrSettings.print.title", { defaultValue: "Tlač QR kódov" })}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("pages.qrSettings.print.help", {
                defaultValue:
                  "Nastavenie QR hárku v Mape stolov. Poradie na karte: logo, názov stola (ak je povolený), QR kód, nadpis (ak je vyplnený), popis (ak je vyplnený).",
              })}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  {t("pages.qrSettings.print.design", { defaultValue: "Dizajn samolepky (vyberte jeden)" })}
                </Typography>
                <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ rowGap: 1.5 }}>
                  {QR_STICKER_TEMPLATES.map((tpl) => {
                    const selected = (printSettings.template || "minimal") === tpl.key;
                    return (
                      <Button
                        key={tpl.key}
                        variant={selected ? "contained" : "outlined"}
                        onClick={() => setPrintSettings((p) => ({ ...p, template: tpl.key }))}
                      >
                        {tpl.label}
                      </Button>
                    );
                  })}
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {t("pages.qrSettings.print.logo", { defaultValue: "Logo (JPG/PNG, max 1 MB)" })}
                </Typography>
                <UploadComponent
                  name="printLogo"
                  value={printSettings.logo || ""}
                  setFieldValue={(_, value) => setPrintSettings((p) => ({ ...p, logo: value || "" }))}
                  onUpload={async (file) => {
                    if (file && file.size > 1024 * 1024) {
                      enqueueSnackbar(
                        t("pages.qrSettings.print.logoTooBig", { defaultValue: "Logo môže mať najviac 1 MB." }),
                        { variant: "error" }
                      );
                      return printSettings.logo || "";
                    }
                    return uploadToImagekit(file, "dsok/qr-print-logo");
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  size="small"
                  fullWidth
                  label={t("pages.qrSettings.print.heading", { defaultValue: "Nadpis" })}
                  value={printSettings.heading || ""}
                  onChange={(e) => setPrintSettings((p) => ({ ...p, heading: e.target.value }))}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {t("pages.qrSettings.print.description", { defaultValue: "Popis (krátky text)" })}
                </Typography>
                <ReactQuill
                  value={printSettings.description || ""}
                  onChange={(content) =>
                    setPrintSettings((p) => ({ ...p, description: content === "<p><br></p>" ? "" : content }))
                  }
                  modules={{ toolbar: [["bold", "italic", "underline"], [{ list: "ordered" }, { list: "bullet" }], ["clean"]] }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {t("pages.qrSettings.print.preview", { defaultValue: "Náhľad samolepky" })}
                </Typography>
                <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1.5, p: 1.5, display: "flex", justifyContent: "center", bgcolor: "#f3f3f3" }}>
                  {previewUrl ? (
                    <Box
                      component="iframe"
                      title="qr-sticker-preview"
                      src={previewUrl}
                      sx={{ width: 300, height: 540, border: 0, borderRadius: 1, bgcolor: "#fff" }}
                    />
                  ) : (
                    <Box sx={{ width: 300, height: 540, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <CircularProgress size={24} />
                    </Box>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                  {t("pages.qrSettings.print.previewHint", {
                    defaultValue: "Náhľad vychádza z aktuálnych (aj neuložených) nastavení so vzorovým stolom. Na hárku v Mape stolov sa tlačia 3 samolepky na riadok.",
                  })}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ) : null}

      {activeTab === 4 ? (
        <Card>
          <CardContent>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
              <AddShoppingCartIcon color="primary" />
              <Typography variant="h4" sx={{ m: 0, flexGrow: 1 }}>
                {t("pages.qrSettings.upsell.title", { defaultValue: "Upsell v košíku" })}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("pages.qrSettings.upsell.help", {
                defaultValue:
                  "Sekcia „Pridáte si?“ v košíku. Keď je zapnutá, hosťovi sa zobrazí až 6 produktov z vybraných kategórií, ktoré ešte nemá v košíku.",
              })}
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={upsell.enabled === true}
                  onChange={(e) => setUpsell((u) => ({ ...u, enabled: e.target.checked }))}
                />
              }
              label={t("pages.qrSettings.upsell.enabled", { defaultValue: "Zobraziť „Pridáte si?“ v košíku" })}
            />

            {upsell.enabled ? (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {t("pages.qrSettings.upsell.categories", { defaultValue: "Kategórie na ponúknutie" })}
                </Typography>
                <Autocomplete
                  multiple
                  size="small"
                  options={catalog.categories || []}
                  value={(catalog.categories || []).filter((c) => upsell.categoryIds.includes(String(c.id)))}
                  getOptionLabel={(o) => (o && o.name) || String(o?.id || "")}
                  isOptionEqualToValue={(o, v) => String(o.id) === String(v?.id)}
                  onChange={(_, vals) => setUpsell((u) => ({ ...u, categoryIds: vals.map((v) => String(v.id)) }))}
                  renderOption={(props, o) => (
                    <Box component="li" {...props} key={o.id} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Avatar variant="rounded" src={o.image || undefined} sx={{ width: 28, height: 28 }} />
                      <span>{o.name || o.id}</span>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("pages.qrSettings.upsell.categoriesPick", { defaultValue: "Vyberte kategórie" })}
                    />
                  )}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                  {t("pages.qrSettings.upsell.categoriesHint", {
                    defaultValue: "Z týchto kategórií sa v košíku ponúkne max 6 produktov, ktoré hosť ešte nemá v objednávke.",
                  })}
                </Typography>
              </Box>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {activeTab === 5 && webProvisioned ? (
        <Card>
          <CardContent>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
              <LanguageIcon color="primary" />
              <Typography variant="h4" sx={{ m: 0, flexGrow: 1 }}>
                {t("pages.qrSettings.web.title", { defaultValue: "Web nastavenia" })}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("pages.qrSettings.web.help", {
                defaultValue:
                  "Nastavenia pre webové objednávanie (objednávka cez prehliadač, nie od stola). Platia pre web; QR objednávanie od stola sa nemení.",
              })}
            </Typography>

            {/* Sub-tabs by content: Všeobecné / Rozvoz / Hodiny a čas / Obrázky (gallery). */}
            <Tabs
              value={webTab}
              onChange={(_, v) => { setWebTab(v); if (v === 4) switchTermsChannel("web"); }}
              variant="scrollable" scrollButtons="auto" sx={{ mb: 2 }}
            >
              <Tab label={t("pages.qrSettings.web.tabGeneral", { defaultValue: "Všeobecné" })} />
              <Tab label={t("pages.qrSettings.web.tabDelivery", { defaultValue: "Rozvoz" })} />
              <Tab label={t("pages.qrSettings.web.tabHours", { defaultValue: "Hodiny a čas" })} />
              <Tab label={t("pages.qrSettings.web.tabImages", { defaultValue: "Obrázky" })} />
              <Tab label={t("pages.qrSettings.web.tabTerms", { defaultValue: "Obchodné podmienky" })} />
            </Tabs>

            {webTab === 0 ? (
              <>
            {/* Web ordering on/off — the single switch that enables/disables the whole web shop. */}
            <FormControlLabel
              control={
                <Switch
                  checked={web.enabled === true}
                  onChange={(e) => setWeb((w) => ({ ...w, enabled: e.target.checked }))}
                />
              }
              label={t("pages.qrSettings.web.enabled", { defaultValue: "Povoliť web objednávanie" })}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
              {t("pages.qrSettings.web.enabledHint", {
                defaultValue:
                  "Keď je vypnuté, web objednávanie nie je dostupné (stránka vráti „nedostupné“). Nezávisí od QR objednávania od stola.",
              })}
            </Typography>

            {/* Browser tab title (document.title) for the web shop. Empty → „Datavision web order". */}
            <TextField
              fullWidth size="small" sx={{ mb: 2 }}
              label={t("pages.qrSettings.web.pageTitle", { defaultValue: "Názov stránky (titulok v prehliadači)" })}
              helperText={t("pages.qrSettings.web.pageTitleHint", { defaultValue: "Zobrazí sa ako názov karty v prehliadači. Prázdne = „Datavision web order“." })}
              placeholder="Datavision web order"
              value={web.pageTitle || ""}
              onChange={(e) => setWeb((w) => ({ ...w, pageTitle: e.target.value }))}
            />

            {/* Company name shown on the web home (near the slider). Empty → the venue name is used. */}
            <TextField
              fullWidth size="small" sx={{ mb: 2 }}
              label={t("pages.qrSettings.web.companyName", { defaultValue: "Názov firmy (na úvode)" })}
              helperText={t("pages.qrSettings.web.companyNameHint", { defaultValue: "Zobrazí sa na úvodnej stránke pri slideri." })}
              value={web.companyName || ""}
              onChange={(e) => setWeb((w) => ({ ...w, companyName: e.target.value }))}
            />

            {/* Venue address — shown to the guest for personal pickup („Osobný odber") and dine-in. */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
              {t("pages.qrSettings.web.addressTitle", { defaultValue: "Adresa prevádzky" })}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              {t("pages.qrSettings.web.addressHint", {
                defaultValue:
                  "Zobrazí sa hosťovi pri spôsoboch „Osobný odber“ a „Na mieste“, aby vedel, kam si prísť po objednávku.",
              })}
            </Typography>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth size="small"
                  label={t("pages.qrSettings.web.addrName", { defaultValue: "Názov prevádzky" })}
                  value={web.address.name}
                  onChange={(e) => setWeb((w) => ({ ...w, address: { ...w.address, name: e.target.value } }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth size="small"
                  label={t("pages.qrSettings.web.addrStreet", { defaultValue: "Ulica a číslo" })}
                  value={web.address.street}
                  onChange={(e) => setWeb((w) => ({ ...w, address: { ...w.address, street: e.target.value } }))}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth size="small"
                  label={t("pages.qrSettings.web.addrPostcode", { defaultValue: "PSČ" })}
                  value={web.address.postcode}
                  onChange={(e) => setWeb((w) => ({ ...w, address: { ...w.address, postcode: e.target.value } }))}
                />
              </Grid>
              <Grid item xs={6} sm={9}>
                <TextField
                  fullWidth size="small"
                  label={t("pages.qrSettings.web.addrCity", { defaultValue: "Mesto" })}
                  value={web.address.city}
                  onChange={(e) => setWeb((w) => ({ ...w, address: { ...w.address, city: e.target.value } }))}
                />
              </Grid>
            </Grid>

            {/* Info cards (home): editable title + text per language; empty → automatic content. */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 0.25 }}>
              {t("pages.qrSettings.web.infoTitle", { defaultValue: "Informačné karty na úvode" })}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              {t("pages.qrSettings.web.infoHint", {
                defaultValue:
                  "Titulok a text 4 kariet (Kde nás nájdete, Osobný odber, Rozvoz, Platba). Ak necháte prázdne, zobrazí sa automatický obsah.",
              })}
            </Typography>
            {languages.length > 1 ? (
              <Tabs value={activeLang} onChange={(_, v) => setActiveLang(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 1 }}>
                {languages.map((l) => (
                  <Tab key={l} value={l} label={LANG_LABEL[l] || l} />
                ))}
              </Tabs>
            ) : null}
            {[
              { key: "find", label: t("pages.qrSettings.web.infoFind", { defaultValue: "Kde nás nájdete" }) },
              { key: "pickup", label: t("pages.qrSettings.web.infoPickup", { defaultValue: "Osobný odber" }) },
              { key: "delivery", label: t("pages.qrSettings.web.infoDelivery", { defaultValue: "Rozvoz" }) },
              { key: "payment", label: t("pages.qrSettings.web.infoPayment", { defaultValue: "Platba" }) },
            ].map((card) => (
              <Box key={card.key} sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1.5, mb: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>{card.label}</Typography>
                <TextField
                  fullWidth size="small" sx={{ mb: 1 }}
                  label={`${t("pages.qrSettings.web.infoCardTitle", { defaultValue: "Titulok" })} (${LANG_LABEL[activeLang] || activeLang})`}
                  value={(web.info[card.key] && web.info[card.key].title && web.info[card.key].title[activeLang]) || ""}
                  onChange={(e) =>
                    setWeb((w) => ({ ...w, info: { ...w.info, [card.key]: { ...w.info[card.key], title: { ...(w.info[card.key].title || {}), [activeLang]: e.target.value } } } }))
                  }
                />
                <TextField
                  fullWidth size="small" multiline minRows={2}
                  label={`${t("pages.qrSettings.web.infoCardText", { defaultValue: "Text" })} (${LANG_LABEL[activeLang] || activeLang})`}
                  value={(web.info[card.key] && web.info[card.key].text && web.info[card.key].text[activeLang]) || ""}
                  onChange={(e) =>
                    setWeb((w) => ({ ...w, info: { ...w.info, [card.key]: { ...w.info[card.key], text: { ...(w.info[card.key].text || {}), [activeLang]: e.target.value } } } }))
                  }
                />
              </Box>
            ))}
              </>
            ) : null}

            {webTab === 1 ? (
              <>
            {/* Delivery fee + minimum order value (web „Rozvoz" mode). */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
              {t("pages.qrSettings.web.deliveryTitle", { defaultValue: "Rozvoz" })}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              {t("pages.qrSettings.web.deliveryHint", {
                defaultValue:
                  "Platí pre spôsob „Rozvoz“. Cena rozvozu sa pripočíta k objednávke; minimálnu cenu musí objednávka dosiahnuť, inak ju nie je možné odoslať na rozvoz.",
              })}
            </Typography>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={6} sm={4}>
                <TextField
                  type="number" fullWidth size="small"
                  inputProps={{ min: 0, step: 0.1 }}
                  label={t("pages.qrSettings.web.deliveryFee", { defaultValue: "Cena rozvozu (€)" })}
                  value={web.delivery.fee}
                  onChange={(e) => setWeb((w) => ({ ...w, delivery: { ...w.delivery, fee: Math.max(0, Number(e.target.value) || 0) } }))}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  type="number" fullWidth size="small"
                  inputProps={{ min: 0, step: 0.1 }}
                  label={t("pages.qrSettings.web.deliveryMinOrder", { defaultValue: "Min. objednávka (€)" })}
                  value={web.delivery.minOrder}
                  onChange={(e) => setWeb((w) => ({ ...w, delivery: { ...w.delivery, minOrder: Math.max(0, Number(e.target.value) || 0) } }))}
                />
              </Grid>
            </Grid>

            {/* Global free delivery (applies when zones are OFF): at/above the amount the fee is 0. */}
            <Grid container spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
              <Grid item xs={12} sm={5}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={web.delivery.freeDelivery === true}
                      onChange={(e) =>
                        setWeb((w) => ({
                          ...w,
                          delivery: { ...w.delivery, freeDelivery: e.target.checked, freeFrom: e.target.checked ? w.delivery.freeFrom : 0 },
                        }))
                      }
                    />
                  }
                  label={t("pages.qrSettings.web.deliveryFreeDelivery", { defaultValue: "Doprava zdarma od sumy" })}
                />
              </Grid>
              {web.delivery.freeDelivery ? (
                <Grid item xs={6} sm={3}>
                  <TextField
                    type="number" fullWidth size="small" required inputProps={{ min: 0, step: 0.1 }}
                    error={!(Number(web.delivery.freeFrom) > 0)}
                    helperText={!(Number(web.delivery.freeFrom) > 0) ? t("pages.qrSettings.web.zoneFreeFromReq", { defaultValue: "Zadajte sumu" }) : ""}
                    label={t("pages.qrSettings.web.zoneFreeFrom", { defaultValue: "Suma (€)" })}
                    value={web.delivery.freeFrom}
                    onChange={(e) => setWeb((w) => ({ ...w, delivery: { ...w.delivery, freeFrom: Math.max(0, Number(e.target.value) || 0) } }))}
                  />
                </Grid>
              ) : null}
            </Grid>

            {/* „Povoliť zóny" — when OFF the global settings above apply; when ON, per-area zones below. */}
            <FormControlLabel
              sx={{ mb: 1 }}
              control={
                <Switch
                  checked={web.delivery.zonesEnabled === true}
                  onChange={(e) => setWeb((w) => ({ ...w, delivery: { ...w.delivery, zonesEnabled: e.target.checked } }))}
                />
              }
              label={t("pages.qrSettings.web.zonesEnabled", { defaultValue: "Povoliť zóny rozvozu" })}
            />
            {web.delivery.zonesEnabled ? (
              <>
            {/* Delivery zones — per-zone fee + VAT + min order + free delivery, and the areas
                (street/district/city) the guest picks from. On the web only the areas are shown
                (never the zone name). When zones are enabled they drive the fee/min per selected area. */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
              {t("pages.qrSettings.web.zonesTitle", { defaultValue: "Zóny rozvozu" })}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              {t("pages.qrSettings.web.zonesHint", {
                defaultValue:
                  "Každá zóna má vlastnú cenu, sadzbu DPH a minimálnu objednávku. Do zóny pridajte ulice, mestské časti alebo mestá — hosť na webe vidí len ich zoznam (nie názov zóny) a cenu. Ak sú zóny vyplnené, majú prednosť pred cenou vyššie.",
              })}
            </Typography>
            {(web.delivery.zones || []).map((zone, zi) => (
              <Box key={zone.id || zi} sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1.5, mb: 1.5 }}>
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth size="small"
                      label={t("pages.qrSettings.web.zoneName", { defaultValue: "Názov zóny (interný)" })}
                      value={zone.name}
                      onChange={(e) =>
                        setWeb((w) => {
                          const zones = w.delivery.zones.map((z, i) => (i === zi ? { ...z, name: e.target.value } : z));
                          return { ...w, delivery: { ...w.delivery, zones } };
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={4} sm={2}>
                    <TextField
                      type="number" fullWidth size="small" inputProps={{ min: 0, step: 0.1 }}
                      label={t("pages.qrSettings.web.zoneFee", { defaultValue: "Cena (€)" })}
                      value={zone.fee}
                      onChange={(e) =>
                        setWeb((w) => {
                          const v = Math.max(0, Number(e.target.value) || 0);
                          const zones = w.delivery.zones.map((z, i) => (i === zi ? { ...z, fee: v } : z));
                          return { ...w, delivery: { ...w.delivery, zones } };
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={4} sm={2}>
                    <TextField
                      select fullWidth size="small"
                      label={t("pages.qrSettings.web.zoneVat", { defaultValue: "DPH (%)" })}
                      value={zone.vatRate}
                      onChange={(e) =>
                        setWeb((w) => {
                          const v = Number(e.target.value);
                          const zones = w.delivery.zones.map((z, i) => (i === zi ? { ...z, vatRate: v } : z));
                          return { ...w, delivery: { ...w.delivery, zones } };
                        })
                      }
                    >
                      {ZONE_VAT_RATES.map((r) => (
                        <MenuItem key={r} value={r}>{r} %</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={4} sm={3}>
                    <TextField
                      type="number" fullWidth size="small" inputProps={{ min: 0, step: 0.1 }}
                      label={t("pages.qrSettings.web.zoneMinOrder", { defaultValue: "Min. objednávka (€)" })}
                      value={zone.minOrder}
                      onChange={(e) =>
                        setWeb((w) => {
                          const v = Math.max(0, Number(e.target.value) || 0);
                          const zones = w.delivery.zones.map((z, i) => (i === zi ? { ...z, minOrder: v } : z));
                          return { ...w, delivery: { ...w.delivery, zones } };
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={1} sx={{ textAlign: "right" }}>
                    <Tooltip title={t("pages.qrSettings.web.zoneRemove", { defaultValue: "Odstrániť zónu" })}>
                      <IconButton
                        size="small" color="error"
                        onClick={() =>
                          setWeb((w) => ({ ...w, delivery: { ...w.delivery, zones: w.delivery.zones.filter((_, i) => i !== zi) } }))
                        }
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>

                {/* Entries (street / district / city) of this zone. */}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1, mb: 0.5 }}>
                  {t("pages.qrSettings.web.zoneEntries", { defaultValue: "Položky zóny (ulica / mestská časť / mesto)" })}
                </Typography>
                {(zone.entries || []).map((entry, ei) => (
                  <Grid container spacing={1} key={ei} alignItems="center" sx={{ mb: 0.5 }}>
                    <Grid item xs={4} sm={3}>
                      <TextField
                        select fullWidth size="small"
                        value={entry.type}
                        onChange={(e) =>
                          setWeb((w) => {
                            const zones = w.delivery.zones.map((z, i) =>
                              i !== zi ? z : { ...z, entries: z.entries.map((en, j) => (j === ei ? { ...en, type: e.target.value } : en)) }
                            );
                            return { ...w, delivery: { ...w.delivery, zones } };
                          })
                        }
                      >
                        <MenuItem value="street">{t("pages.qrSettings.web.entryStreet", { defaultValue: "Ulica" })}</MenuItem>
                        <MenuItem value="district">{t("pages.qrSettings.web.entryDistrict", { defaultValue: "Mestská časť" })}</MenuItem>
                        <MenuItem value="city">{t("pages.qrSettings.web.entryCity", { defaultValue: "Mesto" })}</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={7} sm={8}>
                      <TextField
                        fullWidth size="small"
                        placeholder={t("pages.qrSettings.web.entryName", { defaultValue: "Názov (napr. Hlavná)" })}
                        value={entry.name}
                        onChange={(e) =>
                          setWeb((w) => {
                            const zones = w.delivery.zones.map((z, i) =>
                              i !== zi ? z : { ...z, entries: z.entries.map((en, j) => (j === ei ? { ...en, name: e.target.value } : en)) }
                            );
                            return { ...w, delivery: { ...w.delivery, zones } };
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={1} sx={{ textAlign: "right" }}>
                      <IconButton
                        size="small" color="error"
                        onClick={() =>
                          setWeb((w) => {
                            const zones = w.delivery.zones.map((z, i) =>
                              i !== zi ? z : { ...z, entries: z.entries.filter((_, j) => j !== ei) }
                            );
                            return { ...w, delivery: { ...w.delivery, zones } };
                          })
                        }
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
                <Button
                  size="small" startIcon={<AddBoxIcon />} sx={{ mt: 0.5 }}
                  onClick={() =>
                    setWeb((w) => {
                      const zones = w.delivery.zones.map((z, i) => (i !== zi ? z : { ...z, entries: [...z.entries, { type: "street", name: "" }] }));
                      return { ...w, delivery: { ...w.delivery, zones } };
                    })
                  }
                >
                  {t("pages.qrSettings.web.zoneAddEntry", { defaultValue: "Pridať položku" })}
                </Button>
              </Box>
            ))}
            <Button
              size="small" variant="outlined" startIcon={<AddBoxIcon />} sx={{ mb: 2 }}
              onClick={() => setWeb((w) => ({ ...w, delivery: { ...w.delivery, zones: [...(w.delivery.zones || []), emptyZone()] } }))}
            >
              {t("pages.qrSettings.web.zoneAdd", { defaultValue: "Pridať zónu" })}
            </Button>
              </>
            ) : null}

              </>
            ) : null}

            {webTab === 2 ? (
              <>
            {/* Weekly opening hours — drive the „Na čas" time slots (closed days produce no slots). */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
              {t("pages.qrSettings.web.hoursTitle", { defaultValue: "Otváracie hodiny" })}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              {t("pages.qrSettings.web.hoursHint", {
                defaultValue:
                  "Podľa nich sa hosťovi ponúkajú časy pri objednávke „Na čas“. Prázdny čas od–do znamená otvorené celý deň. Zatvorený deň neponúkne žiadny čas.",
              })}
            </Typography>
            {WEB_DAYS.map((day) => {
              const d = web.openingHours[day] || emptyWebDay();
              const dayLabel = t(`pages.qrSettings.web.day_${day}`, {
                defaultValue: { mon: "Pondelok", tue: "Utorok", wed: "Streda", thu: "Štvrtok", fri: "Piatok", sat: "Sobota", sun: "Nedeľa" }[day],
              });
              const setDay = (k, v) =>
                setWeb((w) => ({ ...w, openingHours: { ...w.openingHours, [day]: { ...w.openingHours[day], [k]: v } } }));
              return (
                <Grid container spacing={1} key={day} alignItems="center" sx={{ mb: 0.5 }}>
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel
                      control={<Switch size="small" checked={d.open !== false} onChange={(e) => setDay("open", e.target.checked)} />}
                      label={dayLabel}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <TextField
                      fullWidth size="small" type="time" disabled={d.open === false}
                      label={t("pages.qrSettings.web.hoursFrom", { defaultValue: "Od" })}
                      InputLabelProps={{ shrink: true }}
                      value={d.from}
                      onChange={(e) => setDay("from", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <TextField
                      fullWidth size="small" type="time" disabled={d.open === false}
                      label={t("pages.qrSettings.web.hoursTo", { defaultValue: "Do" })}
                      InputLabelProps={{ shrink: true }}
                      value={d.to}
                      onChange={(e) => setDay("to", e.target.value)}
                    />
                  </Grid>
                </Grid>
              );
            })}

            {/* Fulfillment time offered in the „Ako si objednávku prevezmete?" modal. */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 0.25 }}>
              {t("pages.qrSettings.web.timeTitle", { defaultValue: "Čas objednávky" })}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              {t("pages.qrSettings.web.timeHint", {
                defaultValue:
                  "Ovláda, aké možnosti času uvidí hosť v kroku prevzatia objednávky. Aspoň jedna by mala byť zapnutá.",
              })}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={web.time.asap.enabled !== false}
                    onChange={(e) => setWeb((w) => ({ ...w, time: { ...w.time, asap: { ...w.time.asap, enabled: e.target.checked } } }))}
                  />
                }
                label={t("pages.qrSettings.web.timeAsap", { defaultValue: "Čo najskôr" })}
              />
              {web.time.asap.enabled !== false ? (
                <TextField
                  type="number" size="small" sx={{ width: 190 }}
                  inputProps={{ min: 1, max: 600 }}
                  label={t("pages.qrSettings.web.timeAsapMinutes", { defaultValue: "Čas prípravy (min)" })}
                  value={web.time.asap.minutes}
                  onChange={(e) => {
                    const n = Math.max(1, Math.min(600, Number(e.target.value) || 0));
                    setWeb((w) => ({ ...w, time: { ...w.time, asap: { ...w.time.asap, minutes: n } } }));
                  }}
                />
              ) : null}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              {t("pages.qrSettings.web.timeAsapHint", {
                defaultValue:
                  "„Čo najskôr“ ponúkne ako prvý čas aktuálny čas + čas prípravy (predvolene 20 min). Čas prípravy je priemerný čas, kým je objednávka hotová.",
              })}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={web.time.scheduled.enabled === true}
                  onChange={(e) => setWeb((w) => ({ ...w, time: { ...w.time, scheduled: { ...w.time.scheduled, enabled: e.target.checked } } }))}
                />
              }
              label={t("pages.qrSettings.web.timeScheduled", { defaultValue: "Na čas (výber termínu)" })}
            />
            {web.time.scheduled.enabled === true ? (
              <Box sx={{ mt: 0.5, mb: 0.5 }}>
                <TextField
                  type="number" size="small" sx={{ width: 220 }}
                  inputProps={{ min: 5, max: 240 }}
                  label={t("pages.qrSettings.web.slotMinutes", { defaultValue: "Interval termínov (min)" })}
                  value={web.time.slotMinutes}
                  onChange={(e) => {
                    const n = Math.max(5, Math.min(240, Number(e.target.value) || 0));
                    setWeb((w) => ({ ...w, time: { ...w.time, slotMinutes: n } }));
                  }}
                />
              </Box>
            ) : null}
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {t("pages.qrSettings.web.timeScheduledHint", {
                defaultValue:
                  "„Na čas“ dovolí hosťovi vybrať konkrétny termín. Termíny sa generujú z otváracích hodín po zadanom intervale (napr. každých 15 min).",
              })}
            </Typography>
              </>
            ) : null}

            {webTab === 3 ? (
              <>
                {/* Web logo — picked from the system images; empty → the QR logo is used as default. */}
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
                  {t("pages.qrSettings.web.logoTitle", { defaultValue: "Logo" })}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  {t("pages.qrSettings.web.logoHint", { defaultValue: "Logo zobrazené v hlavičke webu. Ak nevyberiete, použije sa logo z QR objednávania." })}
                </Typography>
                <Alert severity="info" sx={{ py: 0.25, mb: 1.5 }}>
                  {t("pages.qrSettings.web.logoFormatHint", { defaultValue: "Odporúčaný rozmer loga: ~240×80 px (na šírku), PNG s priehľadným pozadím, do 200 KB." })}
                </Alert>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5, flexWrap: "wrap" }}>
                  {web.logo ? (
                    <Box sx={{ height: 56, px: 1.5, border: 1, borderColor: "divider", borderRadius: 1, display: "flex", alignItems: "center", background: "#fff" }}>
                      <img src={web.logo} alt="" style={{ maxHeight: 44, maxWidth: 180, objectFit: "contain" }} />
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t("pages.qrSettings.web.logoDefault", { defaultValue: "Predvolené: logo z QR objednávania" })}
                    </Typography>
                  )}
                  <Button size="small" variant="outlined" onClick={() => setImgPickerFor("logo")}>
                    {t("pages.qrSettings.web.logoPick", { defaultValue: "Vybrať logo" })}
                  </Button>
                  {web.logo ? (
                    <Button size="small" color="error" onClick={() => setWeb((w) => ({ ...w, logo: "" }))}>
                      {t("pages.qrSettings.web.logoClear", { defaultValue: "Odstrániť" })}
                    </Button>
                  ) : null}
                </Box>

                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
                  {t("pages.qrSettings.web.galleryTitle", { defaultValue: "Obrázky (slider na úvode)" })}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  {t("pages.qrSettings.web.galleryHint", {
                    defaultValue:
                      "Obrázky sa zobrazia ako slider na úvodnej obrazovke webu. Ku každému obrázku zadajte názov a popis v jednotlivých jazykoch.",
                  })}
                </Typography>
                <Alert severity="info" sx={{ py: 0.25, mb: 1.5 }}>
                  {t("pages.qrSettings.web.galleryFormatHint", {
                    defaultValue:
                      "Odporúčaný formát: JPG alebo WebP (na šírku). Rozmer ~1600×600 px (pomer 8:3), do 500 KB.",
                  })}
                </Alert>
                {languages.length > 1 ? (
                  <Tabs value={activeLang} onChange={(_, v) => setActiveLang(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 1 }}>
                    {languages.map((l) => (
                      <Tab key={l} value={l} label={LANG_LABEL[l] || l} />
                    ))}
                  </Tabs>
                ) : null}
                {(web.gallery || []).map((g, gi) => (
                  <Box key={g.id || gi} sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1.5, mb: 1.5 }}>
                    <Grid container spacing={1.5} alignItems="flex-start">
                      <Grid item xs={12} sm={4}>
                        {g.image ? (
                          <Box sx={{ mb: 1, border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
                            <img src={g.image} alt="" style={{ width: "100%", height: 120, objectFit: "cover", display: "block" }} />
                          </Box>
                        ) : (
                          <Box sx={{ mb: 1, height: 120, border: 1, borderColor: "divider", borderRadius: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "text.secondary", fontSize: 13 }}>
                            {t("pages.qrSettings.web.galleryNoImage", { defaultValue: "Bez obrázka" })}
                          </Box>
                        )}
                        <Button size="small" variant="outlined" onClick={() => setImgPickerFor(gi)}>
                          {t("pages.qrSettings.web.galleryPick", { defaultValue: "Vybrať obrázok" })}
                        </Button>
                      </Grid>
                      <Grid item xs={12} sm={7}>
                        <TextField
                          fullWidth size="small" sx={{ mb: 1 }}
                          label={`${t("pages.qrSettings.name", { defaultValue: "Názov" })} (${LANG_LABEL[activeLang] || activeLang})`}
                          value={(g.name && g.name[activeLang]) || ""}
                          onChange={(e) =>
                            setWeb((w) => ({ ...w, gallery: w.gallery.map((it, i) => (i === gi ? { ...it, name: { ...(it.name || {}), [activeLang]: e.target.value } } : it)) }))
                          }
                        />
                        <TextField
                          fullWidth size="small" multiline minRows={2}
                          label={`${t("pages.qrSettings.description", { defaultValue: "Popis" })} (${LANG_LABEL[activeLang] || activeLang})`}
                          value={(g.description && g.description[activeLang]) || ""}
                          onChange={(e) =>
                            setWeb((w) => ({ ...w, gallery: w.gallery.map((it, i) => (i === gi ? { ...it, description: { ...(it.description || {}), [activeLang]: e.target.value } } : it)) }))
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={1} sx={{ textAlign: "right" }}>
                        <Tooltip title={t("pages.qrSettings.web.galleryRemove", { defaultValue: "Odstrániť obrázok" })}>
                          <IconButton size="small" color="error" onClick={() => setWeb((w) => ({ ...w, gallery: w.gallery.filter((_, i) => i !== gi) }))}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
                <Button
                  size="small" variant="outlined" startIcon={<AddBoxIcon />}
                  onClick={() => setWeb((w) => ({ ...w, gallery: [...(w.gallery || []), emptyGalleryItem()] }))}
                >
                  {t("pages.qrSettings.web.galleryAdd", { defaultValue: "Pridať obrázok" })}
                </Button>

                {/* Full system media page in selector mode: browse folders, upload, select — the same
                    complete service as the category image field. onSelect builds the public URL. */}
                <Dialog open={imgPickerFor !== null} onClose={() => setImgPickerFor(null)} maxWidth="lg" fullWidth>
                  <DialogContent sx={{ p: 1 }}>
                    {imgPickerFor !== null ? (
                      <Images
                        layout={"row-reverse"}
                        selectorMode
                        allowed={["jpg", "jpeg", "png", "webp", "svg"]}
                        onCancel={() => setImgPickerFor(null)}
                        onSelect={(image) => {
                          if (!image || !image.filename) { setImgPickerFor(null); return; }
                          const url = `${PROD_URL}/uploads/files/${customerId}/Images/${image.filename}`;
                          const target = imgPickerFor;
                          if (target === "logo") setWeb((w) => ({ ...w, logo: url }));
                          else setWeb((w) => ({ ...w, gallery: w.gallery.map((it, i) => (i === target ? { ...it, image: url } : it)) }));
                          setImgPickerFor(null);
                        }}
                      />
                    ) : null}
                  </DialogContent>
                </Dialog>
              </>
            ) : null}

            {webTab === 4 ? renderTermsEditor({ showChannelToggle: false }) : null}
          </CardContent>
        </Card>
      ) : null}
    </Box>
  );
};

export default QrSettings;
